/**
 * Dashboard — "Soft SaaS Depth" variant (Direction C). Shown in DARK mode.
 *
 * Deep slate with layered surfaces and a cool indigo accent, Figtree body with
 * JetBrains Mono numerals. A metric strip with left severity stripes + pill
 * status chips, and framework rows with sparklines (emphasised endpoint).
 *
 * Presentational only — data arrives as a prop from the page shell. The look is
 * driven by scoped CSS in dashboard-theme.css; this wrapper (.oc-dash-depth) is
 * hidden by CSS in light mode.
 *
 * COLOUR: indigo is IDENTITY (default stripe, links, framework marks) — never a
 * verdict. Severity stripes/values and status pills use the semantic ramp.
 * Sparklines are synthetic/decorative (there is no history in the payload) and
 * are aria-hidden; the rate is always present as text.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TONE_FOR_STATUS, type Tone } from '../../components/ui';
import { FrameworkMark } from '../../components/FrameworkMark';
import { toneForRate, type DashboardVariantProps } from './types';

/** Semantic tone -> severity class ('' = neutral, i.e. the indigo accent stripe). */
const SEV_CLASS: Record<Tone, string> = {
  good: 'good', warning: 'warn', serious: 'serious', critical: 'crit', neutral: '',
};
const PILL_CLASS: Record<Tone, string> = {
  good: 'good', warning: 'warn', serious: 'serious', critical: 'crit', neutral: 'neutral',
};

/** A decorative rising sparkline that ends at the current rate. */
function sparkPoints(rate: number): { points: string; ex: number; ey: number } {
  const ramp = [0.5, 0.58, 0.67, 0.78, 0.9, 1];
  const xs = [2, 18, 34, 50, 66, 82];
  const y = (v: number) => 27 - (Math.max(0, Math.min(100, v)) / 100) * 21;
  const pts = xs.map((x, i) => `${x},${y(rate * (ramp[i] ?? 1)).toFixed(1)}`);
  return { points: pts.join(' '), ex: 82, ey: Number(y(rate).toFixed(1)) };
}

export function DashboardDepth({ data }: DashboardVariantProps) {
  const { t } = useTranslation();

  const overall = data.overallCompliance;
  const notInPlan = data.gaps.byPlanMembership.NOT_IN_PLAN ?? 0;
  const overallTone: Tone = overall !== null ? toneForRate(overall) : 'neutral';
  const overdueTone: Tone = data.plans.overdue > 0 ? 'critical' : 'good';

  const cells: Array<{ label: string; value: string | number; tone: Tone; to: string; hint?: string }> = [
    { label: t('dashboard.overallCompliance'), value: overall !== null ? `${overall}%` : '—', tone: overallTone, to: '/frameworks' },
    { label: t('dashboard.openGaps'), value: data.gaps.total, tone: 'neutral', to: '/gaps' },
    { label: t('dashboard.openPlans'), value: data.plans.open, tone: 'neutral', to: '/plans' },
    { label: t('dashboard.overduePlans'), value: data.plans.overdue, tone: overdueTone, to: '/plans' },
    { label: t('dashboard.consolePolicies', 'Policies'), value: data.policies.active, tone: 'neutral', to: '/policies' },
  ];

  return (
    <div className="oc-dash-depth">
      {/* Header: org + plan chip, assessed count */}
      <div className="dtop">
        <div className="org">
          <span className="sr-only">{t('dashboard.title')}: </span>
          {data.organization.name}
          <span className="plan">{data.organization.plan}</span>
        </div>
        <div className="meta">
          {t('dashboard.consoleAssessedOf', {
            assessed: data.assessedFrameworks,
            licensed: data.licensedFrameworks,
            defaultValue: '{{assessed}} of {{licensed}} frameworks assessed',
          })}
        </div>
      </div>

      {/* Metric strip with left severity stripes */}
      <div className="strip">
        {cells.map((c) => (
          <Link className={`cell ${SEV_CLASS[c.tone]}`} to={c.to} key={c.label}>
            <div className="l">{c.label}</div>
            <div className={`v tnum ${SEV_CLASS[c.tone]}`}>{c.value}</div>
          </Link>
        ))}
      </div>
      {/* notInPlan surfaced for parity with the other variants */}
      <span className="sr-only">{`${notInPlan} ${t('gaps.notInPlan').toLowerCase()}`}</span>

      {/* Framework status with sparklines */}
      <div className="panel">
        <div className="ph">
          <h3>{t('dashboard.frameworkStatus')}</h3>
          <Link to="/frameworks">{t('dashboard.browseFrameworks')}</Link>
        </div>
        {data.frameworks.length === 0 ? (
          <div className="crow" style={{ color: 'var(--mut)' }}>{t('dashboard.noFrameworks')}</div>
        ) : (
          data.frameworks.map((fw) => {
            const href = fw.submissionId ? `/assessments/${fw.submissionId}` : '/assessments';
            const tone: Tone = fw.submissionStatus ? (TONE_FOR_STATUS[fw.submissionStatus] ?? 'neutral') : 'neutral';
            const statusLabel = fw.submissionStatus ? t(`status.${fw.submissionStatus}`) : t('status.NOT_STARTED');
            const rate = fw.complianceRate;
            const sp = sparkPoints(rate ?? 0);
            return (
              <Link className="crow" to={href} key={fw.key}>
                <FrameworkMark
                  frameworkKey={fw.key}
                  size="sm"
                  className="!h-[30px] !w-[30px] !rounded-[9px]"
                />
                <span className="nm">
                  <span>{fw.name}</span>
                  <small>
                    {fw.totalCount > 0
                      ? t('dashboard.consoleAnswered', {
                          answered: fw.answeredCount, total: fw.totalCount,
                          defaultValue: '{{answered}}/{{total}} answered',
                        })
                      : fw.version}
                  </small>
                </span>
                <span className={`pill ${PILL_CLASS[tone]}`}>{statusLabel}</span>
                <svg className="spark" viewBox="0 0 92 30" aria-hidden focusable="false">
                  <polyline points={sp.points} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx={sp.ex} cy={sp.ey} r="3.2" fill="var(--accent)" />
                </svg>
                <span className="num tnum">{rate !== null ? `${rate}%` : '—'}</span>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
